// src/frontend/data/books/BlockchainFundamentals/data/cap4.js

const CAP4 = {
  id: 4,
  slug: "cap4-solana",
  title: "Capítulo 4 — Solana: alta velocidad, Proof of History y smart contracts paralelos",
  sections: [
    {
      heading: "¿Qué es Solana (SOL)?",
      text: [
        "Solana (SOL) es una blockchain de tercera generación diseñada para ofrecer alta velocidad, costos de transacción muy bajos y una gran capacidad de escalado.",
        "Su objetivo principal es servir como infraestructura para aplicaciones descentralizadas (dApps) de alta concurrencia.",
        "Solana busca resolver el problema de la escalabilidad sin renunciar a la seguridad, compitiendo directamente con Ethereum."
      ]
    },
    {
      heading: "Solana como competidor de Ethereum",
      text: [
        "Solana se presenta como uno de los competidores más serios de Ethereum en el ámbito de smart contracts.",
        "A diferencia de Ethereum, Solana fue diseñada desde el inicio para alto rendimiento y paralelización.",
        "Mientras Ethereum avanza por fases de actualización, Solana ofrece hoy una red completamente funcional y usable."
      ]
    },
    {
      heading: "Origen e historia de Solana",
      text: [
        "Solana fue creada por Anatoly Yakovenko, ingeniero con experiencia en sistemas distribuidos y optimización en empresas como Qualcomm y Dropbox.",
        "En 2017 Yakovenko comenzó a trabajar en la idea de Proof of History (PoH).",
        "En 2018 se publica el whitepaper y se funda Solana Labs.",
        "En julio de 2018 se alcanza una primera red experimental con 50 nodos y hasta 65,000 transacciones por segundo.",
        "En 2020 se lanza la mainnet beta de Solana."
      ]
    },
    {
      heading: "Proof of History (PoH): la idea central",
      text: [
        "Proof of History (PoH) no es un consenso independiente, sino un reloj criptográfico.",
        "Su función es ordenar las transacciones de forma verificable usando el tiempo como componente criptográfico.",
        "PoH utiliza funciones llamadas VDF (Funciones de Retraso Verificables) para crear una secuencia temporal imposible de falsificar."
      ]
    },
    {
      heading: "PoH + Proof of Stake (PoS)",
      text: [
        "Solana combina Proof of History con Proof of Stake.",
        "Los validadores realizan staking para participar en el consenso.",
        "PoH permite ordenar transacciones rápidamente, mientras PoS decide quién propone y valida bloques.",
        "Esta combinación permite alta velocidad sin depender de minería intensiva."
      ]
    },
    {
      heading: "Ejemplo simplificado de Proof of History",
      text: [
        "Cada transacción incluye un momento temporal preciso.",
        "La red ordena las transacciones cronológicamente con diferencias de milisegundos.",
        "Esto evita ataques como el doble gasto y asegura el orden exacto de ejecución.",
        "El resultado es una blockchain que puede leerse como una línea de tiempo continua."
      ]
    },
    {
      heading: "Velocidad y producción de bloques",
      text: [
        "Solana produce bloques aproximadamente cada 400 milisegundos.",
        "Esto significa que puede generar varios bloques por segundo.",
        "La latencia extremadamente baja permite aplicaciones en tiempo casi real."
      ]
    },
    {
      heading: "Retos de una red tan rápida",
      list: [
        "Alta demanda de ancho de banda.",
        "Requisitos de hardware más elevados.",
        "Necesidad de sincronización temporal precisa entre nodos."
      ]
    },
    {
      heading: "Turbine: propagación eficiente de bloques",
      text: [
        "Turbine es el protocolo de propagación de datos de Solana.",
        "Divide la información de los bloques en fragmentos para acelerar su distribución.",
        "Primero se envía la metadata crítica y luego el resto del bloque.",
        "Es similar conceptualmente a los compact blocks de Bitcoin."
      ]
    },
    {
      heading: "GulfStream: gestión avanzada de transacciones",
      text: [
        "GulfStream es una mempool avanzada que organiza, prioriza y entrega transacciones a los validadores.",
        "Permite que los validadores trabajen en paralelo: validando un bloque y preparando el siguiente.",
        "También ayuda a mantener la sincronización temporal de la red."
      ]
    },
    {
      heading: "Smart contracts en Solana",
      text: [
        "Solana está diseñada específicamente para ejecutar smart contracts y dApps a gran escala.",
        "Los contratos se escriben principalmente en Rust o C.",
        "Estos lenguajes permiten mayor eficiencia y mejor aprovechamiento del hardware."
      ]
    },
    {
      heading: "Sealevel: ejecución paralela de contratos",
      text: [
        "Sealevel es el motor de ejecución de smart contracts de Solana.",
        "Permite ejecutar múltiples contratos en paralelo, aprovechando CPUs multinúcleo.",
        "Esta paralelización es una de las mayores ventajas técnicas de Solana."
      ]
    },
    {
      heading: "Paralelización y escalabilidad real",
      text: [
        "A diferencia de redes secuenciales, Solana puede ejecutar muchas transacciones simultáneamente.",
        "La red escala no solo con más nodos, sino con mejor hardware.",
        "Esto la hace ideal para aplicaciones con alta demanda y uso masivo."
      ]
    },
    {
      heading: "Casos de uso principales",
      list: [
        "Finanzas descentralizadas (DeFi).",
        "NFT y marketplaces de alto volumen.",
        "Gaming y metaversos.",
        "Pagos rápidos y micropagos.",
        "Aplicaciones Web3 de consumo masivo."
      ]
    },
    {
      heading: "Ecosistema y comunidad",
      text: [
        "Solana cuenta con cientos de proyectos activos en su ecosistema.",
        "Existen wallets, exchanges, protocolos DeFi, herramientas para desarrolladores y plataformas de pago.",
        "La comunidad de desarrolladores es una de las de mayor crecimiento en Web3."
      ]
    },
    {
      heading: "Ventajas de Solana",
      list: [
        "Altísima velocidad y baja latencia.",
        "Comisiones extremadamente bajas.",
        "Ejecución paralela de smart contracts.",
        "Experiencia de usuario fluida.",
        "Ecosistema en rápido crecimiento."
      ]
    },
    {
      heading: "Riesgos y críticas",
      text: [
        "Requisitos de hardware elevados pueden afectar la descentralización.",
        "Historial de interrupciones temporales de la red.",
        "Dependencia de una infraestructura técnica compleja.",
        "Mayor dificultad para auditar todo el stack."
      ]
    },
    {
      heading: "Buenas prácticas al usar Solana",
      list: [
        "Usa wallets oficiales y actualizadas.",
        "Verifica siempre la red antes de enviar fondos.",
        "Prueba con montos pequeños.",
        "Ten en cuenta que velocidad no elimina riesgos."
      ]
    },
    {
      heading: "Conclusión",
      text: [
        "Solana es una de las propuestas tecnológicas más ambiciosas del ecosistema blockchain.",
        "Su combinación de Proof of History, Proof of Stake y ejecución paralela ofrece un rendimiento excepcional.",
        "Sin embargo, su diseño implica compromisos técnicos que deben entenderse.",
        "Solana demuestra que la escalabilidad real es posible, pero siempre con trade-offs."
      ]
    }
  ]
};

export default CAP4;
